/**
 * Shared Exams Table component used by TeacherDashboard and AdminDashboard
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Exam } from '../types';

export type ExamsTableProps = {
    exams: Exam[];
    // Column toggles
    showCreatedBy?: boolean;
    showInstitution?: boolean;
    showCategory?: boolean;
    showStatus?: boolean;
    // Action toggles
    showEdit?: boolean;
    showAssign?: boolean;
    showEvaluate?: boolean;
    showPreview?: boolean;
    showDownload?: boolean;
    // Folder move support (optional)
    folderOptions?: { id: number | null; label: string }[];
    onMoveToFolder?: (examId: number, folderId: number | null) => void;
    // Callbacks
    onDelete?: (examId: number) => void;
};

const ExamsTable: React.FC<ExamsTableProps> = ({
    exams,
    showCreatedBy = false,
    showInstitution = false,
    showCategory = false,
    showStatus = true,
    showEdit = true,
    showAssign = true,
    showEvaluate = true,
    showPreview = true,
    showDownload = true,
    folderOptions,
    onMoveToFolder,
    onDelete,
}) => {
    const [selectedFolders, setSelectedFolders] = useState<Record<number, number | null>>({});
    const handleChange = (examId: number, value: string) => {
        const v = value === 'null' ? null : Number(value);
        setSelectedFolders(prev => ({ ...prev, [examId]: v }));
    };
    const handleMove = (examId: number) => {
        if (!onMoveToFolder) return;
        const target = selectedFolders[examId] ?? null;
        onMoveToFolder(examId, target);
    };
    return (
        <div className="table-container">
            <table className="table">
                <thead>
                    <tr>
                        <th>Exam Title</th>
                        {showCreatedBy && <th>Created By</th>}
                        {showInstitution && <th>Institution</th>}
                        <th>Duration</th>
                        <th>Total Marks</th>
                        {showCategory && <th>Category</th>}
                        {showStatus && <th>Status</th>}
                        <th>Created</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {exams.map((exam) => (
                        <tr key={exam.id}>
                            <td>
                                <strong>{exam.title}</strong>
                                {exam.description && (
                                    <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                                        {exam.description.substring(0, 50)}...
                                    </div>
                                )}
                            </td>
                            {showCreatedBy && (
                                <td>
                                    <div style={{ fontSize: '0.875rem' }}>
                                        {exam.createdByName}
                                    </div>
                                </td>
                            )}
                            {showInstitution && (
                                <td>{exam.institutionName || '-'}</td>
                            )}
                            <td>{exam.durationMinutes}</td>
                            <td>{exam.totalMarks}</td>
                            {showCategory && (
                                <td>{exam.categoryName || '-'}</td>
                            )}
                            {showStatus && (
                                <td>
                                    {exam.isActive ? (
                                        <span className="badge badge-success">Active</span>
                                    ) : (
                                        <span className="badge badge-danger">Inactive</span>
                                    )}
                                </td>
                            )}
                            <td>
                                {exam.createdAt
                                    ? new Date(exam.createdAt).toLocaleDateString()
                                    : 'N/A'}
                            </td>
                            <td>
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {folderOptions && onMoveToFolder && (
                                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                            <select
                                                className="form-input"
                                                style={{ height: '55px', backgroundColor: '#ffffff', color: '#111827' }}
                                                value={String(selectedFolders[exam.id!] ?? 'null')}
                                                onChange={(e) => handleChange(exam.id!, e.target.value)}
                                                title="Move to folder"
                                            >
                                                <option value="null">No folder</option>
                                                {folderOptions.map(opt => (
                                                    <option key={String(opt.id)} value={String(opt.id)}>{opt.label}</option>
                                                ))}
                                            </select>
                                            <button
                                                className="btn btn-outline btn-sm"
                                                onClick={() => handleMove(exam.id!)}
                                                title="Move exam to selected folder"
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                📦 Move
                                            </button>
                                        </div>
                                    )}
                                    {showEdit && (
                                        <Link
                                            to={`/edit-exam/${exam.id}`}
                                            className="btn btn-outline btn-sm"
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            ✏️ Edit
                                        </Link>
                                    )}
                                    {showAssign && (
                                        <Link
                                            to={`/assign-exam/${exam.id}`}
                                            className="btn btn-info btn-sm"
                                            style={{ backgroundColor: '#667eea', borderColor: '#667eea', color: 'white', whiteSpace: 'nowrap' }}
                                        >
                                            📌 Assign
                                        </Link>
                                    )}
                                    {showEvaluate && (
                                        <Link
                                            to={`/evaluate/${exam.id}`}
                                            className="btn btn-success btn-sm"
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            📝 Evaluate
                                        </Link>
                                    )}
                                    {showPreview && (
                                        <Link
                                            to={`/preview-exam/${exam.id}`}
                                            className="btn btn-outline btn-sm"
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            👁️ Preview
                                        </Link>
                                    )}
                                    {showDownload && (
                                        <button
                                            className="btn btn-outline btn-sm"
                                            onClick={() => {
                                                if (!exam.id) return;
                                                // Open a hidden iframe to render the exam and trigger print without opening a new tab
                                                const iframe = document.createElement('iframe');
                                                iframe.style.position = 'fixed';
                                                iframe.style.right = '0';
                                                iframe.style.bottom = '0';
                                                iframe.style.width = '0';
                                                iframe.style.height = '0';
                                                iframe.style.border = '0';
                                                iframe.src = `/preview-exam/${exam.id}?mode=exam`;
                                                const cleanup = () => {
                                                    try { document.body.removeChild(iframe); } catch {}
                                                };
                                                iframe.onload = () => {
                                                    try {
                                                        const w = iframe.contentWindow;
                                                        if (w) {
                                                            const waitForImages = (doc: Document) => new Promise<void>((resolve) => {
                                                                try {
                                                                    const imgs = Array.from(doc.images || []);
                                                                    if (imgs.length === 0) { resolve(); return; }
                                                                    let doneCount = 0;
                                                                    const done = () => { doneCount++; if (doneCount >= imgs.length) resolve(); };
                                                                    imgs.forEach((img) => {
                                                                        const htmlImg = img as HTMLImageElement;
                                                                        if (htmlImg.complete && htmlImg.naturalWidth > 0) {
                                                                            done();
                                                                        } else {
                                                                            htmlImg.addEventListener('load', done, { once: true } as any);
                                                                            htmlImg.addEventListener('error', done, { once: true } as any);
                                                                        }
                                                                    });
                                                                    // Safety timeout in case some images never resolve
                                                                    setTimeout(resolve, 8000);
                                                                } catch {
                                                                    resolve();
                                                                }
                                                            });

                                                            // Give React a moment to render inside the iframe, then wait for images
                                                            setTimeout(() => {
                                                                waitForImages(w.document).then(() => {
                                                                    try { w.focus(); } catch {}
                                                                    try { w.print(); } catch {}
                                                                    // Remove iframe after a short delay; browsers don't expose onafterprint reliably for iframes
                                                                    setTimeout(cleanup, 1500);
                                                                });
                                                            }, 400);
                                                        } else {
                                                            cleanup();
                                                        }
                                                    } catch {
                                                        cleanup();
                                                    }
                                                };
                                                document.body.appendChild(iframe);
                                            }}
                                            style={{ whiteSpace: 'nowrap' }}
                                            title="Download PDF"
                                        >
                                            ⬇️ Download
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={() => exam.id && onDelete && onDelete(exam.id)}
                                        style={{ whiteSpace: 'nowrap' }}
                                    >
                                        🗑 Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ExamsTable;
